export default function CryptoPayment() {
  const paymentFeatures = [
    {
      icon: 'ri-qr-code-line',
      title: 'Instant QR Payments',
      description: 'Generate unique payment addresses instantly – scan, send, receive. Transactions confirmed in seconds, not hours.',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      icon: 'ri-wallet-3-line',
      title: 'Multi-Crypto Support',
      description: 'Accept payments in BTC, ETH, SOL, LTC, USDT, ADA, MATIC, AVAX and more – all managed from one unified dashboard.',
      color: 'from-purple-500 to-pink-600',
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'HD Wallet Security',
      description: 'Hierarchical Deterministic wallets generate new addresses for every transaction – maximum privacy, zero address reuse.',
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Pre-Launch Exclusive',
      description: 'Currently the ONLY way to buy PepCoin before official launch – get early access with crypto payments.',
      color: 'from-orange-500 to-red-600',
    },
    {
      icon: 'ri-notification-3-line',
      title: 'Real-Time Notifications',
      description: 'Instant alerts for every transaction via email, SMS, and webhook – never miss a payment, stay always informed.',
      color: 'from-indigo-500 to-purple-600',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Transaction History',
      description: 'Complete audit trail with blockchain verification, export reports for accounting, and track every satoshi.',
      color: 'from-pink-500 to-rose-600',
    },
  ];

  const cryptoAddresses = [
    { 
      crypto: 'Bitcoin', 
      symbol: 'BTC', 
      icon: 'ri-bit-coin-line', 
      color: 'text-orange-400',
      logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
      description: 'The original cryptocurrency'
    },
    { 
      crypto: 'Ethereum', 
      symbol: 'ETH', 
      icon: 'ri-currency-line', 
      color: 'text-blue-400',
      logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
      description: 'Smart contract platform'
    },
    { 
      crypto: 'Solana', 
      symbol: 'SOL', 
      icon: 'ri-sun-line', 
      color: 'text-purple-400',
      logo: 'https://cryptologos.cc/logos/solana-sol-logo.png',
      description: 'Ultra-fast blockchain'
    },
    { 
      crypto: 'Litecoin', 
      symbol: 'LTC', 
      icon: 'ri-coin-line', 
      color: 'text-gray-400',
      logo: 'https://cryptologos.cc/logos/litecoin-ltc-logo.png',
      description: 'Silver to Bitcoin\'s gold'
    },
    { 
      crypto: 'USDT', 
      symbol: 'USDT', 
      icon: 'ri-money-dollar-circle-line', 
      color: 'text-green-400',
      logo: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
      description: 'Stable value cryptocurrency'
    },
    { 
      crypto: 'Cardano', 
      symbol: 'ADA', 
      icon: 'ri-heart-line', 
      color: 'text-blue-300',
      logo: 'https://cryptologos.cc/logos/cardano-ada-logo.png',
      description: 'Sustainable blockchain'
    },
    { 
      crypto: 'Polygon', 
      symbol: 'MATIC', 
      icon: 'ri-polygon-line', 
      color: 'text-purple-300',
      logo: 'https://cryptologos.cc/logos/polygon-matic-logo.png',
      description: 'Ethereum scaling solution'
    },
    { 
      crypto: 'Avalanche', 
      symbol: 'AVAX', 
      icon: 'ri-mountain-line', 
      color: 'text-red-400',
      logo: 'https://cryptologos.cc/logos/avalanche-avax-logo.png',
      description: 'High-performance DeFi'
    },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header - Compact for Mobile */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-4 sm:mb-6 backdrop-blur-sm">
            <i className="ri-rocket-line text-orange-400 text-sm sm:text-base"></i>
            <span className="text-orange-400 text-xs sm:text-sm font-semibold">Pre-Launch Exclusive</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 text-white px-2">
            Buy PepCoin with Crypto Only
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
            During pre-launch, cryptocurrency is the ONLY way to purchase PepCoin. Get early access now.
          </p>
          
          {/* Pre-Launch Notice - Compact */}
          <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-2xl mx-auto border border-purple-500/30">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-4">
              <i className="ri-time-line text-orange-400 text-lg sm:text-xl"></i>
              <span className="text-orange-400 font-bold text-sm sm:text-base">Crypto Payments Only</span>
            </div>
            <p className="text-gray-300 text-xs sm:text-sm">
              Traditional payment methods available after official launch.
            </p>
          </div>
        </div>

        {/* Supported Cryptocurrencies Grid - Mobile Optimized */}
        <div className="max-w-6xl mx-auto mb-8 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-8 text-center">Accepted Cryptocurrencies</h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {cryptoAddresses.map((crypto, index) => (
              <div
                key={index}
                className="group p-3 sm:p-4 md:p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl hover:border-cyan-500/50 transition-all duration-300 cursor-pointer text-center"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 md:mb-4 bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 flex items-center justify-center">
                  <img 
                    src={crypto.logo} 
                    alt={crypto.crypto}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full hidden items-center justify-center bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg">
                    <i className={`${crypto.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
                  </div>
                </div>
                <div className="text-white font-bold text-sm sm:text-base mb-0.5 sm:mb-1">{crypto.symbol}</div>
                <div className="text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2 hidden sm:block">{crypto.crypto}</div>
                <div className="text-xs text-gray-500 hidden md:block">{crypto.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Buy - Simplified and Compact */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 p-4 sm:p-6 md:p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">How to Buy</h3>
          
          <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            <div className="text-center p-3 sm:p-4 md:p-6 bg-black/30 rounded-lg sm:rounded-xl border border-white/10">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                <span className="text-cyan-400 font-bold text-sm sm:text-base md:text-xl">1</span>
              </div>
              <h4 className="text-white font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">Choose</h4>
              <p className="text-gray-400 text-xs hidden sm:block">Select crypto</p>
            </div>
            
            <div className="text-center p-3 sm:p-4 md:p-6 bg-black/30 rounded-lg sm:rounded-xl border border-white/10">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                <span className="text-purple-400 font-bold text-sm sm:text-base md:text-xl">2</span>
              </div>
              <h4 className="text-white font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">Send</h4>
              <p className="text-gray-400 text-xs hidden sm:block">Make payment</p>
            </div>
            
            <div className="text-center p-3 sm:p-4 md:p-6 bg-black/30 rounded-lg sm:rounded-xl border border-white/10">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                <span className="text-green-400 font-bold text-sm sm:text-base md:text-xl">3</span>
              </div>
              <h4 className="text-white font-bold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">Receive</h4>
              <p className="text-gray-400 text-xs hidden sm:block">Get PepCoin</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg sm:rounded-xl font-bold text-white text-sm sm:text-base hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
              <i className="ri-shopping-cart-line mr-1 sm:mr-2"></i>
              Buy Now
            </button>
            <button className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-white/5 border border-white/20 rounded-lg sm:rounded-xl font-bold text-white text-sm sm:text-base hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
              <i className="ri-question-line mr-1 sm:mr-2"></i>
              Learn More
            </button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {paymentFeatures.map((feature, index) => (
            <div
              key={index}
              className="group p-4 sm:p-6 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl hover:border-cyan-500/50 transition-all duration-500 hover:scale-105 cursor-pointer"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center mb-3 sm:mb-4 bg-gradient-to-br ${feature.color} rounded-lg sm:rounded-xl shadow-lg transition-all duration-300`}>
                <i className={`${feature.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
              </div>
              <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3 text-white group-hover:text-cyan-400 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed text-xs sm:text-sm">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Launch Timeline CTA - Compact */}
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-cyan-500/10 backdrop-blur-sm p-6 sm:p-8 md:p-12">
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-500/20 border border-orange-500/30 rounded-full mb-4 sm:mb-6">
              <i className="ri-time-line text-orange-400 text-sm"></i>
              <span className="text-orange-400 text-xs sm:text-sm font-semibold">Limited Time</span>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4 px-2">
              Get PepCoin Before Everyone Else
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Buy with crypto now before traditional payment methods launch.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
              <div className="p-3 sm:p-4 bg-green-500/20 border border-green-500/30 rounded-lg sm:rounded-xl">
                <div className="text-green-400 font-bold text-sm sm:text-base md:text-lg mb-1">✓ Now</div>
                <div className="text-gray-300 text-xs sm:text-sm">Crypto</div>
              </div>
              <div className="p-3 sm:p-4 bg-orange-500/20 border border-orange-500/30 rounded-lg sm:rounded-xl">
                <div className="text-orange-400 font-bold text-sm sm:text-base md:text-lg mb-1">⏳ Soon</div>
                <div className="text-gray-300 text-xs sm:text-sm">Fiat Payments</div>
              </div>
              <div className="p-3 sm:p-4 bg-purple-500/20 border border-purple-500/30 rounded-lg sm:rounded-xl">
                <div className="text-purple-400 font-bold text-sm sm:text-base md:text-lg mb-1">🚀 Launch</div>
                <div className="text-gray-300 text-xs sm:text-sm">All Methods</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg sm:rounded-xl font-bold text-white text-sm sm:text-base hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
                <i className="ri-shopping-cart-line mr-1 sm:mr-2"></i>
                Buy with Crypto
              </button>
              <button className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white/5 border border-white/20 rounded-lg sm:rounded-xl font-bold text-white text-sm sm:text-base hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
                <i className="ri-notification-line mr-1 sm:mr-2"></i>
                Notify for Fiat
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
